import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Plus, Trash2, Upload, X, FileText, Image as ImageIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Separator } from '../components/ui/separator';
import { formatEuro } from '../utils/format';
import { Progress } from '../components/ui/progress';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PropertyForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  // Plantilla de gastos por defecto
  const defaultGastos = [
    { descripcion: 'Notaria', monto: 1200 },
    { descripcion: 'Registro', monto: 700 },
    { descripcion: 'Gestoría', monto: 350 },
    { descripcion: 'Seguridad (Puerta /Alarma)', monto: 2000 },
    { descripcion: 'Arreglos y pintura', monto: 1100 },
    { descripcion: 'Gastos Admin (Cert. Energético, Cédula Hab.)', monto: 250 },
    { descripcion: 'Gastos Juridicos / Okupa', monto: 10000 },
    { descripcion: 'Honorarios Venta', monto: 3000 }
  ];

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    direccion: '',
    cp: '',
    poblacion: '',
    referencia_catastral: '',
    fecha_entrada: new Date().toISOString().split('T')[0],
    fecha_compra: '',
    fecha_venta: '',
    fuente: '',
    comercial: '',
    observaciones: '',
    precio_compra: 0,
    precio_venta: 0,
    precio_referencia: 0,
    superficie: 0,
    habitaciones: 0,
    banos: 0,
    ocupado: false,
    ascensor: false,
    estado: 'Activo',
    gastos: defaultGastos
  });

  const [calculations, setCalculations] = useState({
    itp: 0,
    gastos_totales: 0,
    beneficio: 0,
    porcentaje_beneficio: 0
  });

  // Estados para archivos pendientes de subir
  const [pendingPhotos, setPendingPhotos] = useState([]);
  const [pendingDocs, setPendingDocs] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');
  const [dragOverPhotos, setDragOverPhotos] = useState(false);
  const [dragOverDocs, setDragOverDocs] = useState(false);

  useEffect(() => {
    if (isEdit) {
      loadProperty();
    }
  }, [id]);

  useEffect(() => {
    calculateFields();
  }, [formData.precio_compra, formData.precio_venta, formData.precio_referencia, formData.gastos]);

  const loadProperty = async () => {
    try {
      const response = await axios.get(`${API}/properties/${id}`);
      setFormData(response.data);
    } catch (error) {
      console.error('Error al cargar inmueble:', error);
      toast.error('Error al cargar el inmueble');
    }
  };

  const calculateFields = () => {
    const precioCompra = parseFloat(formData.precio_compra) || 0;
    const precioVenta = parseFloat(formData.precio_venta) || 0;
    const precioReferencia = parseFloat(formData.precio_referencia) || 0;

    const baseItp = Math.max(precioReferencia, precioCompra);
    const itp = baseItp * 0.10;

    const gastosTotales = formData.gastos.reduce((sum, g) => sum + (parseFloat(g.monto) || 0), 0);

    const beneficio = precioVenta - (precioCompra + gastosTotales + itp);

    const inversionTotal = precioCompra + gastosTotales + itp;
    const porcentajeBeneficio = inversionTotal > 0 ? (beneficio / inversionTotal) * 100 : 0;

    setCalculations({ itp, gastos_totales: gastosTotales, beneficio, porcentaje_beneficio: porcentajeBeneficio });
  };

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : value
    }));
  };

  const handleSwitchChange = (value) => {
    setFormData(prev => ({ ...prev, estado: value }));
  };

  const addGasto = () => {
    setFormData(prev => ({
      ...prev,
      gastos: [...prev.gastos, { descripcion: '', monto: 0 }]
    }));
  };

  const updateGasto = (index, field, value) => {
    const newGastos = [...formData.gastos];
    newGastos[index][field] = field === 'monto' ? (parseFloat(value) || 0) : value;
    setFormData(prev => ({ ...prev, gastos: newGastos }));
  };

  const removeGasto = (index) => {
    setFormData(prev => ({
      ...prev,
      gastos: prev.gastos.filter((_, i) => i !== index)
    }));
  };

  // Funciones para manejar fotos pendientes
  const handleAddPhotos = (e) => {
    const files = Array.from(e.target.files);
    addPhotosFromFiles(files);
    e.target.value = '';
  };

  const addPhotosFromFiles = (files) => {
    const validFiles = files.filter(file => file.type.startsWith('image/'));
    if (validFiles.length !== files.length) {
      toast.error('Solo se permiten archivos de imagen');
    }
    if (validFiles.length > 0) {
      setPendingPhotos(prev => [...prev, ...validFiles]);
      toast.success(`${validFiles.length} foto(s) añadida(s)`);
    }
  };

  const removePhoto = (index) => {
    setPendingPhotos(prev => prev.filter((_, i) => i !== index));
  };

  // Drag & Drop para fotos
  const handlePhotoDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverPhotos(true);
  };

  const handlePhotoDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverPhotos(false);
  };

  const handlePhotoDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverPhotos(false);
    const files = Array.from(e.dataTransfer.files);
    addPhotosFromFiles(files);
  };

  // Funciones para manejar documentos pendientes
  const handleAddDoc = (e) => {
    const file = e.target.files[0];
    addDocFromFile(file);
    e.target.value = '';
  };

  const addDocFromFile = (file) => {
    if (file && file.type === 'application/pdf') {
      const docName = file.name.replace('.pdf', '');
      setPendingDocs(prev => [...prev, { file, descripcion: docName }]);
      toast.success('Documento añadido');
    } else if (file) {
      toast.error('Solo se permiten archivos PDF');
    }
  };

  // Drag & Drop para documentos
  const handleDocDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverDocs(true);
  };

  const handleDocDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverDocs(false);
  };

  const handleDocDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverDocs(false);
    const files = Array.from(e.dataTransfer.files);
    files.forEach(file => addDocFromFile(file));
  };

  const updateDocDescripcion = (index, descripcion) => {
    setPendingDocs(prev => prev.map((doc, i) => 
      i === index ? { ...doc, descripcion } : doc
    ));
  };

  const removeDoc = (index) => {
    setPendingDocs(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setUploadProgress(0);
    setUploadStatus('');

    try {
      let propertyId = id;

      if (isEdit) {
        await axios.put(`${API}/properties/${id}`, formData);
        toast.success('Inmueble actualizado correctamente');
      } else {
        // Crear el inmueble primero
        setUploadStatus('Creando inmueble...');
        const response = await axios.post(`${API}/properties`, formData);
        propertyId = response.data.id;
        toast.success('Inmueble creado correctamente');
      }

      // Subir fotos si hay pendientes
      const totalFiles = pendingPhotos.length + pendingDocs.length;
      let uploadedCount = 0;

      if (pendingPhotos.length > 0) {
        setUploadStatus('Subiendo fotos...');
        for (const photo of pendingPhotos) {
          const photoFormData = new FormData();
          photoFormData.append('file', photo);
          await axios.post(`${API}/properties/${propertyId}/photos`, photoFormData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          uploadedCount++;
          setUploadProgress(Math.round((uploadedCount / totalFiles) * 100));
        }
      }

      // Subir documentos si hay pendientes
      if (pendingDocs.length > 0) {
        setUploadStatus('Subiendo documentos...');
        for (const doc of pendingDocs) {
          const docFormData = new FormData();
          docFormData.append('file', doc.file);
          docFormData.append('descripcion', doc.descripcion);
          await axios.post(`${API}/properties/${propertyId}/documents`, docFormData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          uploadedCount++;
          setUploadProgress(Math.round((uploadedCount / totalFiles) * 100));
        }
      }

      if (totalFiles > 0) {
        toast.success(`${totalFiles} archivo(s) subido(s) correctamente`);
      }

      navigate('/properties');
    } catch (error) {
      console.error('Error al guardar inmueble:', error);
      toast.error('Error al guardar el inmueble');
    } finally {
      setLoading(false);
      setUploadStatus('');
      setUploadProgress(0);
    }
  };

  return (
    <div className="p-8 max-w-5xl mx-auto space-y-8">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate('/properties')}
            data-testid="back-btn"
          >
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="text-xl md:text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {isEdit ? 'Editar Inmueble' : 'Nuevo Inmueble'}
            </h1>
            <p className="text-xs text-muted-foreground mt-1">Complete la información del inmueble</p>
          </div>
        </div>
        
        {isEdit && (
          <div className="flex gap-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => navigate('/properties')}
              data-testid="cancel-btn-top"
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleSubmit}
              disabled={loading}
              data-testid="submit-btn-top"
              className="shadow-md"
            >
              {loading ? 'Guardando...' : 'Actualizar'}
            </Button>
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        <Card className="p-8 shadow-sm">
          <h2 className="text-2xl font-semibold mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>Información General</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {isEdit && (
              <div>
                <Label htmlFor="ref">ID (Serie)</Label>
                <Input
                  id="ref"
                  name="ref"
                  data-testid="ref-input"
                  value={formData.ref || ''}
                  onChange={handleChange}
                  placeholder="2026/001"
                  className="mt-2"
                />
                <p className="text-xs text-muted-foreground mt-1">Formato: AAAA/XXX</p>
              </div>
            )}
            
            <div className={isEdit ? '' : 'md:col-span-2'}>
              <Label htmlFor="direccion">Dirección *</Label>
              <Input
                id="direccion"
                name="direccion"
                data-testid="direccion-input"
                value={formData.direccion}
                onChange={handleChange}
                required
                placeholder="Calle, número"
                className="mt-2"
              />
            </div>

            <div>
              <Label htmlFor="cp">Código Postal</Label>
              <Input
                id="cp"
                name="cp"
                data-testid="cp-input"
                value={formData.cp}
                onChange={handleChange}
                placeholder="28001"
                maxLength={5}
                className="mt-2"
              />
            </div>

            <div>
              <Label htmlFor="poblacion">Población *</Label>
              <Input
                id="poblacion"
                name="poblacion"
                data-testid="poblacion-input"
                value={formData.poblacion}
                onChange={handleChange}
                required
                placeholder="Ciudad o municipio"
                className="mt-2"
              />
            </div>

            <div>
              <Label htmlFor="referencia_catastral">Referencia Catastral</Label>
              <Input
                id="referencia_catastral"
                name="referencia_catastral"
                data-testid="referencia-input"
                value={formData.referencia_catastral}
                onChange={handleChange}
                placeholder="Ej: 802411DF2860D0002XG"
                className="mt-2"
              />
            </div>

            <div>
              <Label htmlFor="fecha_entrada">Fecha de Entrada</Label>
              <Input
                id="fecha_entrada"
                name="fecha_entrada"
                type="date"
                data-testid="fecha-entrada-input"
                value={formData.fecha_entrada}
                onChange={handleChange}
                className="mt-2"
              />
            </div>

            <div>
              <Label htmlFor="fecha_compra">Fecha de Compra</Label>
              <Input
                id="fecha_compra"
                name="fecha_compra"
                type="date"
                data-testid="fecha-compra-input"
                value={formData.fecha_compra}
                onChange={handleChange}
                className="mt-2"
              />
            </div>

            <div>
              <Label htmlFor="fecha_venta">Fecha de Venta</Label>
              <Input
                id="fecha_venta"
                name="fecha_venta"
                type="date"
                data-testid="fecha-venta-input"
                value={formData.fecha_venta}
                onChange={handleChange}
                className="mt-2"
              />
            </div>

            <div>
              <Label htmlFor="fuente">Fuente</Label>
              <Input
                id="fuente"
                name="fuente"
                data-testid="fuente-input"
                value={formData.fuente}
                onChange={handleChange}
                placeholder="Ej: Idealista, Fotocasa..."
                className="mt-2"
              />
            </div>

            <div>
              <Label htmlFor="comercial">Comercial</Label>
              <Input
                id="comercial"
                name="comercial"
                data-testid="comercial-input"
                value={formData.comercial}
                onChange={handleChange}
                placeholder="Nombre del comercial"
                className="mt-2"
              />
            </div>

            <div>
              <Label htmlFor="estado">Estado</Label>
              <Select value={formData.estado} onValueChange={handleSwitchChange}>
                <SelectTrigger className="mt-2" data-testid="estado-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Activo">Activo</SelectItem>
                  <SelectItem value="Ofertado">Ofertado</SelectItem>
                  <SelectItem value="Arras">Arras</SelectItem>
                  <SelectItem value="En cartera">En cartera</SelectItem>
                  <SelectItem value="Reservado">Reservado</SelectItem>
                  <SelectItem value="Vendido">Vendido</SelectItem>
                  <SelectItem value="Descartado">Descartado</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="md:col-span-2">
              <Label htmlFor="observaciones">Observaciones</Label>
              <Textarea
                id="observaciones"
                name="observaciones"
                data-testid="observaciones-input"
                value={formData.observaciones}
                onChange={handleChange}
                placeholder="Notas adicionales sobre el inmueble..."
                rows={4}
                className="mt-2"
              />
            </div>
          </div>
        </Card>

        <Card className="p-8 shadow-sm">
          <h2 className="text-2xl font-semibold mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>Características</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <Label htmlFor="superficie">Superficie (m²)</Label>
              <Input
                id="superficie"
                name="superficie"
                type="number"
                data-testid="superficie-input"
                value={formData.superficie}
                onChange={handleChange}
                min="0"
                step="0.01"
                className="mt-2"
              />
            </div>

            <div>
              <Label htmlFor="habitaciones">Habitaciones</Label>
              <Input
                id="habitaciones"
                name="habitaciones"
                type="number"
                data-testid="habitaciones-input"
                value={formData.habitaciones}
                onChange={handleChange}
                min="0"
                className="mt-2"
              />
            </div>

            <div>
              <Label htmlFor="banos">Baños</Label>
              <Input
                id="banos"
                name="banos"
                type="number"
                data-testid="banos-input"
                value={formData.banos}
                onChange={handleChange}
                min="0"
                className="mt-2"
              />
            </div>
          </div>

          <Separator className="my-6" />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label className="mb-3 block">Ocupado</Label>
              <RadioGroup
                value={formData.ocupado ? "si" : "no"}
                onValueChange={(value) => setFormData(prev => ({ ...prev, ocupado: value === "si" }))}
                data-testid="ocupado-radio"
              >
                <div className="flex items-center space-x-6">
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="si" id="ocupado-si" />
                    <Label htmlFor="ocupado-si" className="cursor-pointer font-normal">Sí</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="no" id="ocupado-no" />
                    <Label htmlFor="ocupado-no" className="cursor-pointer font-normal">No</Label>
                  </div>
                </div>
              </RadioGroup>
            </div>

            <div>
              <Label className="mb-3 block">Ascensor</Label>
              <RadioGroup
                value={formData.ascensor ? "si" : "no"}
                onValueChange={(value) => setFormData(prev => ({ ...prev, ascensor: value === "si" }))}
                data-testid="ascensor-radio"
              >
                <div className="flex items-center space-x-6">
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="si" id="ascensor-si" />
                    <Label htmlFor="ascensor-si" className="cursor-pointer font-normal">Sí</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="no" id="ascensor-no" />
                    <Label htmlFor="ascensor-no" className="cursor-pointer font-normal">No</Label>
                  </div>
                </div>
              </RadioGroup>
            </div>
          </div>
        </Card>

        <Card className="p-8 shadow-sm">
          <h2 className="text-2xl font-semibold mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>Precios</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <Label htmlFor="precio_compra">Precio de Compra * (€)</Label>
              <Input
                id="precio_compra"
                name="precio_compra"
                type="number"
                data-testid="precio-compra-input"
                value={formData.precio_compra}
                onChange={handleChange}
                required
                min="0"
                step="0.01"
                className="mt-2"
              />
            </div>

            <div>
              <Label htmlFor="precio_venta">Precio de Venta (€)</Label>
              <Input
                id="precio_venta"
                name="precio_venta"
                type="number"
                data-testid="precio-venta-input"
                value={formData.precio_venta}
                onChange={handleChange}
                min="0"
                step="0.01"
                className="mt-2"
              />
            </div>

            <div>
              <Label htmlFor="precio_referencia">Precio de Referencia (€)</Label>
              <Input
                id="precio_referencia"
                name="precio_referencia"
                type="number"
                data-testid="precio-referencia-input"
                value={formData.precio_referencia}
                onChange={handleChange}
                min="0"
                step="0.01"
                className="mt-2"
              />
            </div>
          </div>
        </Card>

        <Card className="p-8 shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>Gastos</h2>
            <Button type="button" variant="outline" onClick={addGasto} data-testid="add-gasto-btn">
              <Plus className="mr-2" size={16} />
              Añadir Gasto
            </Button>
          </div>
          {formData.gastos.length > 0 ? (
            <div className="space-y-4">
              {formData.gastos.map((gasto, index) => (
                <div key={index} className="flex gap-4 items-end">
                  <div className="flex-1">
                    <Label>Descripción</Label>
                    <Input
                      value={gasto.descripcion}
                      onChange={(e) => updateGasto(index, 'descripcion', e.target.value)}
                      placeholder="Ej: Notario, Registro, Gestoría"
                      data-testid={`gasto-descripcion-${index}`}
                      className="mt-2"
                    />
                  </div>
                  <div className="w-40">
                    <Label>Importe (€)</Label>
                    <Input
                      type="number"
                      value={gasto.monto}
                      onChange={(e) => updateGasto(index, 'monto', e.target.value)}
                      min="0"
                      step="0.01"
                      data-testid={`gasto-monto-${index}`}
                      className="mt-2"
                    />
                  </div>
                  <Button 
                    type="button" 
                    variant="destructive" 
                    size="icon"
                    onClick={() => removeGasto(index)}
                    data-testid={`remove-gasto-${index}`}
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-8">No hay gastos añadidos</p>
          )}
        </Card>

        {/* Sección de Fotos con Drag & Drop */}
        <Card className="p-8 shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>Fotos</h2>
            <div>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleAddPhotos}
                id="photo-input"
                className="hidden"
                data-testid="photo-input"
              />
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => document.getElementById('photo-input').click()}
                data-testid="add-photo-btn"
              >
                <Upload className="mr-2" size={16} />
                Añadir Fotos
              </Button>
            </div>
          </div>
          <div
            className={`drop-zone border-2 border-dashed rounded-lg transition-all ${
              dragOverPhotos ? 'drag-over border-primary bg-primary/5' : 'border-muted-foreground/25'
            }`}
            onDragOver={handlePhotoDragOver}
            onDragLeave={handlePhotoDragLeave}
            onDrop={handlePhotoDrop}
          >
            {pendingPhotos.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4">
                {pendingPhotos.map((photo, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={URL.createObjectURL(photo)}
                      alt={`Preview ${index + 1}`}
                      className="w-full h-32 object-cover rounded-lg border"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="icon"
                      className="absolute top-2 right-2 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={() => removePhoto(index)}
                      data-testid={`remove-photo-${index}`}
                    >
                      <X size={14} />
                    </Button>
                    <p className="text-xs text-muted-foreground mt-1 truncate">{photo.name}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center text-muted-foreground">
                <ImageIcon className="mx-auto mb-2" size={32} />
                <p className="font-medium">Arrastra imágenes aquí</p>
                <p className="text-xs mt-1">o haz clic en "Añadir Fotos"</p>
              </div>
            )}
          </div>
        </Card>

        {/* Sección de Documentos con Drag & Drop */}
        <Card className="p-8 shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>Documentos PDF</h2>
            <div>
              <input
                type="file"
                accept="application/pdf"
                onChange={handleAddDoc}
                id="doc-input"
                className="hidden"
                data-testid="doc-input"
              />
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => document.getElementById('doc-input').click()}
                data-testid="add-doc-btn"
              >
                <Upload className="mr-2" size={16} />
                Añadir Documento
              </Button>
            </div>
          </div>
          <div
            className={`drop-zone border-2 border-dashed rounded-lg transition-all ${
              dragOverDocs ? 'drag-over border-primary bg-primary/5' : 'border-muted-foreground/25'
            }`}
            onDragOver={handleDocDragOver}
            onDragLeave={handleDocDragLeave}
            onDrop={handleDocDrop}
          >
            {pendingDocs.length > 0 ? (
              <div className="space-y-3 p-4">
                {pendingDocs.map((doc, index) => (
                  <div key={index} className="flex items-center gap-4 p-3 bg-muted/50 rounded-lg">
                    <FileText className="text-red-500 flex-shrink-0" size={24} />
                    <div className="flex-1">
                      <Input
                        value={doc.descripcion}
                        onChange={(e) => updateDocDescripcion(index, e.target.value)}
                        placeholder="Descripción del documento"
                        className="mb-1"
                        data-testid={`doc-descripcion-${index}`}
                      />
                      <p className="text-xs text-muted-foreground">{doc.file.name}</p>
                    </div>
                    <Button
                      type="button"
                      variant="destructive"
                      size="icon"
                      onClick={() => removeDoc(index)}
                      data-testid={`remove-doc-${index}`}
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center text-muted-foreground">
                <FileText className="mx-auto mb-2" size={32} />
                <p className="font-medium">Arrastra PDFs aquí</p>
                <p className="text-xs mt-1">o haz clic en "Añadir Documento"</p>
              </div>
            )}
          </div>
        </Card>

        <Card className="p-8 shadow-sm bg-muted/30">
          <h2 className="text-2xl font-semibold mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>Cálculos Automáticos</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-background p-4 rounded-lg border">
              <p className="text-sm text-muted-foreground mb-2">ITP (10%)</p>
              <p className="text-2xl font-bold text-primary" data-testid="calc-itp">
                {formatEuro(calculations.itp)}
              </p>
            </div>
            <div className="bg-background p-4 rounded-lg border">
              <p className="text-sm text-muted-foreground mb-2">Gastos Totales</p>
              <p className="text-2xl font-bold" data-testid="calc-gastos">
                {formatEuro(calculations.gastos_totales)}
              </p>
            </div>
            <div className="bg-background p-4 rounded-lg border">
              <p className="text-sm text-muted-foreground mb-2">Beneficio</p>
              <p className={`text-2xl font-bold ${calculations.beneficio >= 0 ? 'text-green-600' : 'text-red-600'}`} data-testid="calc-beneficio">
                {formatEuro(calculations.beneficio)}
              </p>
            </div>
            <div className="bg-background p-4 rounded-lg border">
              <p className="text-sm text-muted-foreground mb-2">% Beneficio</p>
              <p className={`text-2xl font-bold ${calculations.porcentaje_beneficio >= 0 ? 'text-green-600' : 'text-red-600'}`} data-testid="calc-porcentaje">
                {calculations.porcentaje_beneficio.toFixed(2)}%
              </p>
            </div>
          </div>
        </Card>

        <div className="flex flex-col gap-4">
          {loading && uploadStatus && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>{uploadStatus}</span>
                <span>{uploadProgress}%</span>
              </div>
              <Progress value={uploadProgress} className="h-2" />
            </div>
          )}
          <div className="flex justify-end gap-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => navigate('/properties')}
              data-testid="cancel-btn"
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button 
              type="submit" 
              disabled={loading}
              data-testid="submit-btn"
              className="shadow-md"
            >
              {loading ? (uploadStatus || 'Guardando...') : (isEdit ? 'Actualizar' : 'Crear Inmueble')}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}